/**
 * The edge treatments this tab draws on a panel.
 *
 * `accentBar` marks a block as a quotation — the audit's headline reading, or the wording of
 * a question being reported on. `edgeLine` outlines an item in a list, so the items in one
 * list read alike and only their tone tells them apart. `PLACEHOLDER_BOX` outlines a block
 * that holds no reading, because there was nothing to compute one from.
 *
 * Kept apart on purpose: a reader should be able to tell a quoted block from a list item at
 * a glance, so the accent bar is not spent on things that are merely coloured.
 */
import { token, type ExtendedTokenPath } from '@amzn/stencil-design-tokens/js/web/utils';

/** One width for every accented panel, so the summary and the question callout agree. */
const BAR_WIDTH = 'dimensions.border.width.400';

/** One width for every outlined item, so a list reads as one list. */
const LINE_WIDTH = 'dimensions.border.width.100';

/**
 * Left-edge accent bar for a quoted panel, as a style object for the `css` prop.
 *
 * Stencil's `border` prop sets all four edges and it exposes no per-edge prop, so a
 * single-edge rule has to go through `css`.
 *
 * Call this at module scope and reuse the result. A fresh object per render would give
 * emotion a new style to diff on every pass.
 *
 * @param statusColorToken - Status colour the panel carries, e.g.
 *   `color.status.info-border`. Typed from `token` itself, so an unknown name is a
 *   compile error here — unlike the layout props, which accept any string.
 */
export function accentBar(statusColorToken: ExtendedTokenPath): {
  readonly borderLeft: string;
} {
  return {
    borderLeft: `${token(BAR_WIDTH)} solid ${token(statusColorToken)}`,
  } as const;
}

/**
 * Outline for one item in a list, as a style object for the `css` prop.
 *
 * Goes through `css` rather than the `border` prop for two separate reasons. A `View` parses
 * that prop by splitting on spaces and only reads a width that starts with a digit, so a
 * width given as a token silently loses its colour. A `Card` spreads its own border after
 * the props it was given, so the prop is discarded outright.
 *
 * Call this at module scope and reuse the result, as with `accentBar`.
 *
 * @param statusColorToken - Status colour the item carries, e.g.
 *   `color.status.neutral-border`.
 */
export function edgeLine(statusColorToken: ExtendedTokenPath): {
  readonly border: string;
} {
  return {
    border: `${token(LINE_WIDTH)} solid ${token(statusColorToken)}`,
  } as const;
}

/**
 * Outline for a block that says why it holds no reading, as a style object for the `css` prop.
 *
 * Dashed, so the block reads as a space a reading would have filled rather than as something
 * the app is announcing. A solid banner in a status colour would give a routine first audit
 * the same weight as a failure or an outage.
 *
 * Takes no colour: this treatment carries no status, which is the point of it. Carries only
 * the border, for the reason given on `edgeLine` — the corner radius is a layout prop, so it
 * belongs on the component rather than in here.
 */
export const PLACEHOLDER_BOX = {
  border: `${token(LINE_WIDTH)} dashed ${token('color.border.primary')}`,
} as const;
